import { useState, useEffect, useCallback } from 'react';
import './GoogleSheetsLoader.css';

// Ensure API URL has protocol
const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_BASE_URL = rawApiUrl.startsWith('http://') || rawApiUrl.startsWith('https://') 
  ? rawApiUrl 
  : `https://${rawApiUrl}`;

function GoogleSheetsLoader({ onDataLoaded, onError, dataLoaded, refreshTrigger }) {
  const [, setLoading] = useState(!dataLoaded);
  const [, setError] = useState(null);

  const fetchGoogleSheetsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use backend API to get data with actual row numbers
      const response = await fetch(`${API_BASE_URL}/api/google-sheets-data`);
      if (!response.ok) {
        throw new Error(`Failed to fetch Google Sheets: ${response.statusText}`);
      }

      const data = await response.json();
      const headers = data.headers || [];
      const rowsWithNumbers = data.rows || [];
      
      // Find subscription_end column index (column with header "סוף מנוי")
      const subscriptionEndColIndex = headers.findIndex(h => h === 'סוף מנוי');

      // Map rows to ad objects (each row already has the actual row number)
      const ads = rowsWithNumbers.map((rowData, index) => {
        const actualRowNumber = rowData.rowNumber; // This is the actual row number from Google Sheets (from backend API)
        const row = rowData.data; // Array of cell values (strings)
        
        if (!row || row.length === 0) return null;

        // Map columns to ad structure
        // Column indices: A=0, B=1, C=2, D=3, E=4, F=5, G=6, H=7, I=8, J=9, K=10, L=11, M=12, N=13, O=14, P=15, ..., U=20, V=21
        const getCellValue = (colIndex) => {
          if (!row || colIndex >= row.length || !row[colIndex]) return '';
          return String(row[colIndex] || '').trim();
        };

        // Column A (index 0): Timestamp
        const timestamp = getCellValue(0); // Column A
        
        // Column U (index 20): Ad Created - store this value for filtering
        const adCreated = getCellValue(20); // Column U

        // Extract Google Drive image URL and convert to direct link
        // Column K may contain multiple comma-separated URLs - use the first one
        const imageUrlRaw = getCellValue(10); // Column K - Google Drive link(s)
        const imageUrl = imageUrlRaw ? imageUrlRaw.split(',')[0].trim() : ''; // Take first URL if multiple
        let processedImageUrl = imageUrl;
        if (imageUrl && imageUrl.includes('drive.google.com')) {
          // Convert Google Drive share link to direct image link
          // Handle different Google Drive URL formats:
          // - https://drive.google.com/file/d/FILE_ID/view
          // - https://drive.google.com/open?id=FILE_ID
          // - https://drive.google.com/d/FILE_ID
          let fileId = null;
          const dMatch = imageUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
          const idMatch = imageUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);
          
          if (dMatch) {
            fileId = dMatch[1];
          } else if (idMatch) {
            fileId = idMatch[1];
          } else {
          }
          
          if (fileId) {
            // Use CORS proxy as primary to bypass CORS restrictions
            // The proxy fetches the image and serves it with proper CORS headers
            const directUrl = `https://drive.google.com/uc?export=view&id=${fileId}`;
            processedImageUrl = `https://images.weserv.nl/?url=${encodeURIComponent(directUrl)}&w=1000`;
          } else {
          }
        } else if (imageUrl) {
        }

        // Extract Google Drive QR code URL or direct URL
        const qrCodeUrl = getCellValue(21); // Column V
        
        // Extract preview video URL
        const previewVideoUrl = getCellValue(22); // Column W
        let processedQrCodeUrl = qrCodeUrl;
        if (qrCodeUrl && qrCodeUrl.includes('drive.google.com')) {
          let fileId = null;
          const dMatch = qrCodeUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
          const idMatch = qrCodeUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);
          
          if (dMatch) {
            fileId = dMatch[1];
          } else if (idMatch) {
            fileId = idMatch[1];
          }
          
          if (fileId) {
            // Use uc?export=view for QR code images as well
            processedQrCodeUrl = `https://drive.google.com/uc?export=view&id=${fileId}`;
          }
        }

        // Extract Vimeo/YouTube ID or Google Drive link
        const vimeoValue = getCellValue(15); // Column P
        let vimeoId = null;
        // Support Vimeo ID, YouTube ID, or Google Drive link
        if (vimeoValue && vimeoValue.trim().length > 0) {
          vimeoId = vimeoValue.trim();
          // Video ID or Google Drive link stored in vimeoId
        }

        const finalImageUrl = processedImageUrl || imageUrl;

        // Use the actual row number from Google Sheets (provided by backend API)
        const sheetsAdLineNumber = actualRowNumber;
        
        const ad = {
          id: `sheets-${index + 1}`, // Display ID (1-based for user display)
          title: getCellValue(2), // Column C - שם מלא (business name as title)
          name: getCellValue(2), // Column C - שם מלא
          description: getCellValue(11), // Column L - Description
          vimeo: vimeoId, // Column P - Vimeo/YouTube ID or Google Drive link
          staticQRCODE: processedQrCodeUrl || qrCodeUrl, // Column V - QR code destination
          titleQRCODE: getCellValue(13), // Column N - QR code title
          media: {
            0: {
              image: finalImageUrl, // Column K - Image URL
              name: getCellValue(12) // Column M - Image title
            }
          },
          // Additional fields for ad creation
          business_email: getCellValue(1), // Column B
          business_name: getCellValue(2), // Column C - שם מלא
          business_phone: getCellValue(3), // Column D - מספר טלפון
          whatsapp_phone: getCellValue(8), // Column I - מספר טלפון לוואטסאפ
          business_description: getCellValue(6), // Column G
          business_website: getCellValue(7), // Column H
          youtubeID: vimeoId, // Column P - Vimeo/YouTube ID or Google Drive link
          paid_until: subscriptionEndColIndex >= 0 ? getCellValue(subscriptionEndColIndex) : '', // Subscription end column
          // Legacy fields for compatibility
          phone: getCellValue(3), // Column D - מספר טלפון
          businessName: getCellValue(2), // Column C - שם מלא
          timestamp: timestamp, // Column A - Timestamp
          adCreated: adCreated, // Column U - Ad Created (for filtering)
          previewVideo: previewVideoUrl || '', // Column W - Preview Video URL
          source: 'google-sheets',
          // Store the original row number from Google Sheets for updating back
          // This is the actual row number in Google Sheets (row 1 is header, row 2 is first data row)
          sheetsAdLineNumber: sheetsAdLineNumber,
          // Store raw data for reference
          _rawData: {
            rowIndex: sheetsAdLineNumber, // Keep for backward compatibility
            displayIndex: index + 1, // Display index (1-based for user display)
            headers: headers
          }
        };


        return ad;
      }).filter(ad => ad !== null && (ad.vimeo || ad.media?.[0]?.image)); // Filter out empty rows and ads without image/video

      setLoading(false);
      if (onDataLoaded) {
        onDataLoaded(ads);
      }
    } catch (err) {
      console.error('Error fetching Google Sheets data:', err);
      setError(err.message || 'Failed to load data from Google Sheets');
      setLoading(false);
      if (onError) {
        onError(err.message || 'Failed to load data from Google Sheets');
      }
    }
  }, [onDataLoaded, onError]);

  useEffect(() => {
    // Fetch data if:
    // 1. Data hasn't been loaded yet, OR
    // 2. refreshTrigger changed (triggering a refresh)
    if (!dataLoaded || refreshTrigger) {
      fetchGoogleSheetsData();
    }
  }, [dataLoaded, refreshTrigger, fetchGoogleSheetsData]);

  // This component doesn't render anything - it just fetches data
  // The loading/error states are handled by AdminList via props
  return null;
}

export default GoogleSheetsLoader;

